const ANONYMOUS_ID_KEY = 'jaosve_analytics_anonymous_id';
const SESSION_ID_KEY = 'jaosve_analytics_session_id';
const SENSITIVE_KEYS = ['password', 'token', 'secret', 'authorization', 'email', 'phone', 'lozinka'];

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const ANALYTICS_API_BASE_URL = process.env.REACT_APP_ANALYTICS_API_URL || '';

const memoryStorage = {};
const pendingPayloads = [];
let flushListenersRegistered = false;

function getBrowserStorage(storageName) {
  try {
    if (typeof window === 'undefined') return null;
    return window[storageName] || null;
  } catch {
    return null;
  }
}

function safeStorageGet(storageName, key) {
  try {
    const storage = getBrowserStorage(storageName);
    const value = storage ? storage.getItem(key) : null;
    return value || memoryStorage[key] || null;
  } catch {
    return memoryStorage[key] || null;
  }
}

function safeStorageSet(storageName, key, value) {
  memoryStorage[key] = value;
  try {
    const storage = getBrowserStorage(storageName);
    if (!storage) return;
    storage.setItem(key, value);
  } catch {
    // Ignore storage failures; analytics should never block the app.
  }
}

function createId(prefix) {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID()}`;
  }

  const randomPart = Math.random().toString(36).slice(2);
  const timePart = Date.now().toString(36);
  return `${prefix}_${timePart}_${randomPart}`;
}

export function getAnonymousId() {
  const existing = safeStorageGet('localStorage', ANONYMOUS_ID_KEY);
  if (existing) return existing;

  const anonymousId = createId('anon');
  safeStorageSet('localStorage', ANONYMOUS_ID_KEY, anonymousId);
  return anonymousId;
}

export function getSessionId() {
  const existing = safeStorageGet('sessionStorage', SESSION_ID_KEY);
  if (existing) return existing;

  const sessionId = createId('session');
  safeStorageSet('sessionStorage', SESSION_ID_KEY, sessionId);
  return sessionId;
}

function cleanText(value, maxLength = 500) {
  if (value === null || value === undefined) return undefined;
  const text = String(value).replace(/\s+/g, ' ').trim();
  return text ? text.slice(0, maxLength) : undefined;
}

function isSensitiveKey(key) {
  const lowered = String(key).toLowerCase();
  return SENSITIVE_KEYS.some((sensitive) => lowered.includes(sensitive));
}

function sanitizeValue(value, depth = 0) {
  if (value === null || value === undefined || typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') return cleanText(value, 500);
  if (Array.isArray(value)) return value.slice(0, 25).map((item) => sanitizeValue(item, depth + 1));
  if (typeof value === 'object' && depth < 2) return sanitizeProperties(value, depth + 1);
  return cleanText(value, 250);
}

function sanitizeProperties(properties = {}, depth = 0) {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return {};

  return Object.entries(properties).slice(0, 40).reduce((cleaned, [key, value]) => {
    if (isSensitiveKey(key)) return cleaned;

    const cleanKey = cleanText(key, 80);
    const cleanValue = sanitizeValue(value, depth);
    if (cleanKey && cleanValue !== undefined) {
      cleaned[cleanKey] = cleanValue;
    }
    return cleaned;
  }, {});
}

function uniqueValues(values) {
  return values.filter(Boolean).filter((value, index, array) => array.indexOf(value) === index);
}

export function getAnalyticsApiBaseUrls() {
  const urls = [ANALYTICS_API_BASE_URL, API_BASE_URL];

  if (typeof window !== 'undefined' && !window.jaosveNative && !/^https:\/\//.test(API_BASE_URL) && ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    urls.push('http://localhost:5000/api');
    urls.push('http://127.0.0.1:5000/api');
    urls.push('http://localhost/api');
  }

  return uniqueValues(urls);
}

function getAnalyticsUrl(apiBaseUrl) {
  return `${String(apiBaseUrl || '').replace(/\/+$/, '')}/events`;
}

function getNavigatorConnection() {
  if (typeof navigator === 'undefined') return {};
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!connection) return {};

  return {
    connection_type: cleanText(connection.effectiveType || connection.type, 80),
    connection_downlink: typeof connection.downlink === 'number' ? connection.downlink : undefined,
    connection_rtt: typeof connection.rtt === 'number' ? connection.rtt : undefined,
    connection_save_data: typeof connection.saveData === 'boolean' ? connection.saveData : undefined,
  };
}

function getUserAgentData() {
  if (typeof navigator === 'undefined' || !navigator.userAgentData) return {};

  return {
    ua_brands: Array.isArray(navigator.userAgentData.brands)
      ? navigator.userAgentData.brands.slice(0, 5).map((brand) => ({
        brand: cleanText(brand.brand, 80),
        version: cleanText(brand.version, 40),
      }))
      : undefined,
    ua_mobile: typeof navigator.userAgentData.mobile === 'boolean'
      ? navigator.userAgentData.mobile
      : undefined,
    ua_platform: cleanText(navigator.userAgentData.platform, 120),
  };
}

function getDeviceProperties() {
  if (typeof window === 'undefined') return {};

  const userAgent = window.navigator?.userAgent || '';
  const hasCoarsePointer = typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  const resolvedTimeZone = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return undefined;
    }
  })();

  return {
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    screen_width: window.screen?.width,
    screen_height: window.screen?.height,
    screen_avail_width: window.screen?.availWidth,
    screen_avail_height: window.screen?.availHeight,
    color_depth: window.screen?.colorDepth,
    pixel_depth: window.screen?.pixelDepth,
    device_pixel_ratio: window.devicePixelRatio,
    is_mobile: hasCoarsePointer || /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent),
    platform: cleanText(window.navigator?.platform, 120),
    vendor: cleanText(window.navigator?.vendor, 120),
    language: cleanText(window.navigator?.language, 80),
    languages: Array.isArray(window.navigator?.languages)
      ? window.navigator.languages.slice(0, 8).map((language) => cleanText(language, 40)).filter(Boolean)
      : undefined,
    timezone: cleanText(resolvedTimeZone, 120),
    timezone_offset_minutes: new Date().getTimezoneOffset(),
    cookies_enabled: typeof window.navigator?.cookieEnabled === 'boolean'
      ? window.navigator.cookieEnabled
      : undefined,
    do_not_track: cleanText(
      window.navigator?.doNotTrack || window.doNotTrack || window.navigator?.msDoNotTrack,
      40
    ),
    max_touch_points: typeof window.navigator?.maxTouchPoints === 'number'
      ? window.navigator.maxTouchPoints
      : undefined,
    hardware_concurrency: typeof window.navigator?.hardwareConcurrency === 'number'
      ? window.navigator.hardwareConcurrency
      : undefined,
    device_memory_gb: typeof window.navigator?.deviceMemory === 'number'
      ? window.navigator.deviceMemory
      : undefined,
    online: typeof window.navigator?.onLine === 'boolean' ? window.navigator.onLine : undefined,
    ...getNavigatorConnection(),
    ...getUserAgentData(),
  };
}

async function postAnalyticsEvent(apiBaseUrl, payload, headers, keepalive) {
  const options = {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  };

  if (keepalive) {
    options.keepalive = true;
  }

  return fetch(getAnalyticsUrl(apiBaseUrl), options);
}

function sendAnalyticsBeacon(payload) {
  try {
    if (
      typeof navigator === 'undefined' ||
      typeof navigator.sendBeacon !== 'function'
    ) {
      return false;
    }

    const body = JSON.stringify(payload);
    const beaconBody = typeof Blob === 'function'
      ? new Blob([body], { type: 'application/json' })
      : body;

    for (const apiBaseUrl of getAnalyticsApiBaseUrls()) {
      if (navigator.sendBeacon(getAnalyticsUrl(apiBaseUrl), beaconBody)) {
        return true;
      }
    }
  } catch {
    // Beacon is best-effort only.
  }

  return false;
}

function removePendingPayload(payload) {
  const index = pendingPayloads.indexOf(payload);
  if (index >= 0) {
    pendingPayloads.splice(index, 1);
  }
}

function flushPendingPayloads() {
  if (!pendingPayloads.length) return;

  const payloads = pendingPayloads.slice();
  for (const payload of payloads) {
    if (sendAnalyticsBeacon(payload)) {
      removePendingPayload(payload);
    }
  }
}

function ensureFlushListeners() {
  if (flushListenersRegistered || typeof window === 'undefined') return;
  flushListenersRegistered = true;

  window.addEventListener('pagehide', flushPendingPayloads);

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushPendingPayloads();
      }
    });
  }
}

function queuePayloadForFlush(payload) {
  ensureFlushListeners();
  pendingPayloads.push(payload);
  if (pendingPayloads.length > 20) {
    pendingPayloads.shift();
  }
}

export async function trackEvent(eventName, options = {}) {
  try {
    const cleanEventName = cleanText(eventName, 100);
    if (!cleanEventName || typeof fetch !== 'function') {
      return Promise.resolve(null);
    }

    const payload = {
      event_name: cleanEventName,
      anonymous_id: getAnonymousId(),
      session_id: getSessionId(),
      page: cleanText(options.page || window.location.pathname, 500),
      target: cleanText(options.target, 500),
      properties: sanitizeProperties({
        ...getDeviceProperties(),
        ...(options.properties || {}),
        referrer: document.referrer ? cleanText(document.referrer, 500) : undefined,
      }),
    };
    queuePayloadForFlush(payload);

    const headers = { 'Content-Type': 'application/json' };
    const token = safeStorageGet('localStorage', 'token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    for (const apiBaseUrl of getAnalyticsApiBaseUrls()) {
      try {
        const response = await postAnalyticsEvent(apiBaseUrl, payload, headers, false);
        if (response.ok) {
          removePendingPayload(payload);
          return response;
        }
        if (![404, 502, 503, 504].includes(response.status)) {
          removePendingPayload(payload);
          return response;
        }
      } catch {
        // Retry with keepalive below; some mobile browsers reject one transport but not the other.
      }

      try {
        const response = await postAnalyticsEvent(apiBaseUrl, payload, headers, true);
        if (response.ok) {
          removePendingPayload(payload);
          return response;
        }
        if (![404, 502, 503, 504].includes(response.status)) {
          removePendingPayload(payload);
          return response;
        }
      } catch {
        // Try the next analytics backend candidate.
      }
    }

    if (sendAnalyticsBeacon(payload)) {
      removePendingPayload(payload);
      return { ok: true, beacon: true };
    }

    return null;
  } catch {
    return Promise.resolve(null);
  }
}

export function trackPageView(page, properties = {}) {
  return trackEvent('page_view', {
    page,
    target: page,
    properties,
  });
}
